import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import axios from 'axios';
import { Shield, Activity, Lock, AlertTriangle, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { ConnectDhanModal, DhanPortfolioWidget } from '@/components/dhan/DhanModals';
import { API } from '@/App';

const TradingDashboard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectModalOpen, setConnectModalOpen] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/trading/dhan-status`, { withCredentials: true });
      setStatus(res.data);
    } catch (err) {
      toast.error('Failed to load Dhan connection status');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.post(`${API}/trading/disconnect-dhan`, {}, { withCredentials: true });
      toast.success('Disconnected from Dhan');
      fetchStatus();
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  if (loading) return (
    <DashboardLayout title="Trading Dashboard">
      <div className="p-6 md:p-12 max-w-7xl mx-auto min-h-screen text-gray-900 dark:text-white font-outfit">
        <h1 className="text-3xl font-bold mb-8 flex items-center gap-3">
          <Activity className="text-[#00FF88]" /> Trading Dashboard
        </h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Skeleton Card 1 */}
          <div className="bg-gray-100 dark:bg-white/5 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl p-6 animate-pulse">
            <div className="h-6 w-1/3 bg-white/10 rounded mb-4"></div>
            <div className="space-y-4">
              <div className="h-12 w-full bg-gray-100 dark:bg-white/5 rounded-lg"></div>
              <div className="h-12 w-full bg-gray-100 dark:bg-white/5 rounded-lg"></div>
              <div className="h-12 w-full bg-gray-100 dark:bg-white/5 rounded-lg mt-4"></div>
            </div>
          </div>
          
          {/* Skeleton Card 2 */}
          <div className="bg-gray-100 dark:bg-white/5 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl p-6 animate-pulse">
            <div className="h-6 w-1/3 bg-white/10 rounded mb-4"></div>
            <div className="space-y-4">
              <div className="h-12 w-full bg-gray-100 dark:bg-white/5 rounded-lg"></div>
              <div className="h-16 w-full bg-gray-100 dark:bg-white/5 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout title="Trading Dashboard">
      <div className="p-6 md:p-12 max-w-7xl mx-auto min-h-screen text-gray-900 dark:text-white font-outfit">
        <h1 className="text-3xl font-bold mb-8 flex items-center gap-3">
          <Activity className="text-[#00FF88]" /> Trading Dashboard
        </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Connection Status Card */}
        <div className="bg-gray-100 dark:bg-white/5 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl p-6 shadow-2xl relative overflow-hidden group hover:border-[#00FF88]/30 transition-all duration-500">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#00FF88]/5 rounded-full blur-3xl -mr-10 -mt-10 transition-all duration-500 group-hover:bg-[#00FF88]/10"></div>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Shield className="text-[#00FF88]" /> Broker Connection
          </h2>
          
          {status?.connected ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center bg-gray-100 dark:bg-white/5 backdrop-blur-md p-4 rounded-lg border border-gray-200 dark:border-white/5">
                <span className="text-gray-400">Dhan Client ID</span>
                <span className="font-mono">{status.client_id}</span>
              </div>
              <div className="flex justify-between items-center bg-gray-100 dark:bg-white/5 backdrop-blur-md p-4 rounded-lg border border-gray-200 dark:border-white/5">
                <span className="text-gray-400">Token Status</span>
                <span className={`font-bold ${status.expired ? 'text-red-500 drop-shadow-[0_0_8px_rgba(239,68,68,0.5)]' : 'text-[#00FF88] drop-shadow-[0_0_8px_rgba(0,255,136,0.5)]'}`}>
                  {status.expired ? 'EXPIRED' : 'ACTIVE'}
                </span>
              </div>
              {!status.expired && (
                <div className="text-sm text-gray-500">
                  Expires in {status.hours_remaining} hours
                </div>
              )}
              
              <button onClick={handleDisconnect} className="w-full py-3 bg-red-500/10 text-red-500 border border-red-500/20 rounded-lg hover:bg-red-500/20 font-bold transition-all">
                Disconnect Broker
              </button>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400 mb-6">No broker connected. Connect your Dhan account to enable live execution.</p>
              <button onClick={() => setConnectModalOpen(true)} className="px-6 py-3 bg-[#00FF88] text-black rounded-lg font-bold hover:brightness-110">
                Connect Dhan Account
              </button>
            </div>
          )}
        </div>

        {/* Global Safety Card */}
        <div className="bg-gray-100 dark:bg-white/5 backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl p-6 shadow-2xl relative overflow-hidden group hover:border-yellow-500/30 transition-all duration-500">
          <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-500/5 rounded-full blur-3xl -mr-10 -mt-10 transition-all duration-500 group-hover:bg-yellow-500/10"></div>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Lock className="text-[#00FF88]" /> Risk & Safety Profile
          </h2>
          <div className="space-y-4 relative z-10">
             <div className="flex justify-between items-center bg-gray-100 dark:bg-white/5 backdrop-blur-md p-4 rounded-lg border-l-4 border-red-500 shadow-lg">
                <span className="text-gray-400">Trading Mode</span>
                <span className="font-bold text-red-500 animate-pulse drop-shadow-[0_0_8px_rgba(239,68,68,0.5)]">LIVE TRADING</span>
              </div>
              <div className="text-sm text-gray-400 p-4 bg-gray-100 dark:bg-white/5 rounded-lg flex gap-3 border border-gray-200 dark:border-white/5">
                <AlertTriangle className="text-red-500 shrink-0" />
                Live trading is enabled. All executed orders will be sent directly to the broker and affect real capital.
              </div>
          </div>
        </div>
      </div>
      
      {/* Portfolios and Orders Widget */}
      <div className="mt-8">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <DollarSign className="text-[#00FF88]" /> Portfolio & Funds
        </h2>
        <DhanPortfolioWidget />
      </div>

      <ConnectDhanModal 
        open={connectModalOpen} 
        onOpenChange={setConnectModalOpen} 
        onConnected={fetchStatus} 
      />
      </div>
    </DashboardLayout>
  );
};

export default TradingDashboard;
